/**
 * The single source of truth for Wisetack's `application_status` column.
 *
 * Wisetack reports enrollment as nine raw statuses; the app renders seven
 * enrollment states. The five "still filling the form" statuses all collapse
 * into `in_progress`, which is the part nobody remembers correctly — the
 * merchant report parser and the settings normalizer each grew their own copy
 * of the table, and a status added to one would have rendered as
 * `not_configured` through the other.
 *
 * WHY IT LIVES IN A PACKAGE. The staging status simulator lets a tenant user
 * pick a raw status and watch the UI resolve it, which only proves anything if
 * the dropdown offers the statuses the parser actually maps. The frontend needs
 * the list, operations needs the mapping, and neither can import the other — so
 * both read this file. It ships as a `.cjs` + `.d.cts` pair on a package
 * subpath, straight from `src` with no build step, the same way
 * `membership-line` does, so jest, the Nest build, vite and vitest all load it.
 */
const WISETACK_APPLICATION_STATUS_MAP = Object.freeze({
  PENDING: "in_progress",
  INITIATOR_ADDED: "in_progress",
  BUSINESS_ADDED: "in_progress",
  OWNERS_ADDED: "in_progress",
  EXECUTIVE_ADDED: "in_progress",
  APPLICATION_SUBMITTED: "submitted",
  APPLICATION_APPROVED: "approved",
  APPLICATION_DECLINED: "declined",
  EXPIRED: "expired",
});

/** Report order: the application walks down this list as the merchant fills it in. */
const WISETACK_APPLICATION_STATUSES = Object.freeze(
  Object.keys(WISETACK_APPLICATION_STATUS_MAP),
);

/** The mapped enrollment state, or null when Wisetack sent a status we do not know. */
function mapWisetackApplicationStatus(value) {
  if (typeof value !== "string") return null;
  return WISETACK_APPLICATION_STATUS_MAP[value.trim().toUpperCase()] ?? null;
}

module.exports = {
  WISETACK_APPLICATION_STATUSES,
  WISETACK_APPLICATION_STATUS_MAP,
  mapWisetackApplicationStatus,
};
